<script lang="ts">
export interface FacetedFilterProps<TData extends RowData, TKey extends DeepKeys<TData>> {
	column: Column<TData, DeepValue<TData, TKey>>;
	options?: FacetedKeyOptions<TData, TKey>;
	maxCount?: number;
}
</script>

<script setup lang="ts" generic="TData extends RowData, TKey extends DeepKeys<TData>">
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from "@mdi/js";
import type { Column, DeepKeys, DeepValue, RowData } from "@tanstack/vue-table";
import { computed, ref } from "vue";
import { useScreenSize } from "../../composables/screen-size";
import { useLocale } from "../../locale";
import Badge from "../badge/Badge.vue";
import { Command } from "../command";
import { Icon } from "../icon";
import Popover from "../popover/Popover.vue";
import { Tooltip } from "../tooltip";
import type { FacetedKeyOptions } from "./composables/faceted-types";
import { useFilterableColumn } from "./composables/filterable-column";

const props = defineProps<FacetedFilterProps<TData, TKey>>();

const { t } = useLocale();

const { optionDisplay, selectedValues, selectedCount, facets, choices, title } =
	useFilterableColumn(
		() => props.column,
		() => props.options
	);

const maxSelectionCount = computed(() => (props.maxCount != undefined ? props.maxCount : 2));

function handleOnSelect(option: string) {
	const selectedSet = new Set(selectedValues.value);
	const isSelected = selectedSet.has(option);

	if (!props.options?.allowMultiple) {
		selectedSet.clear();
	}

	if (isSelected) {
		selectedSet.delete(option);
	} else {
		selectedSet.add(option);
	}

	const filterValues = Array.from(selectedSet);
	props.column.setFilterValue(filterValues.length === 0 ? undefined : filterValues);

	if (!props.options?.allowMultiple) {
		menuOpen.value = false;
	}
}

function isSelected(option: string) {
	return selectedValues.value.includes(option);
}

const menuOpen = ref(false);

function clearFilter() {
	props.column?.setFilterValue(undefined);
	menuOpen.value = false;
}

const { mdOrLower } = useScreenSize();
</script>

<template>
	<div class="fieldset max-w-60">
		<span class="fieldset-label grow gap-2 text-inherit">
			{{ title }}
			<Tooltip v-if="options?.hint" :tooltip="options.hint">
				<Icon size="sm" :d="mdiInformationOutline" />
			</Tooltip>
		</span>
		<Popover
			v-model:open="menuOpen"
			align="start"
			:side="mdOrLower ? 'bottom' : 'right'"
			matchWidth
		>
			<template #trigger>
				<button
					class="select select-sm"
					:multiple="selectedCount > 1 && selectedCount <= maxSelectionCount ? '' : undefined"
				>
					<div class="flex flex-wrap items-center gap-2">
						<Badge
							v-if="selectedCount > maxSelectionCount"
							size="sm"
							color="primary"
							variant="soft"
						>
							{{ selectedCount }} {{ t(selectedCount > 1 ? "selectedPlural" : "selected") }}
						</Badge>
						<template v-else v-for="(selected, idx) of selectedValues" :key="idx">
							<Badge size="sm" color="primary" variant="soft">
								{{ optionDisplay(selected as any) }}
							</Badge>

							<span v-if="idx !== selectedValues.length - 1" class="opacity-70">
								{{ t("or") }}
							</span>
						</template>
					</div>
					<Icon class="ml-auto" :d="mdOrLower ? mdiChevronDown : mdiChevronRight" />
				</button>
			</template>

			<Command
				:placeholder="title"
				:options="choices"
				optionLabel="label"
				optionValue="value"
				:hideSearch="!props.options?.allowSearch"
				:sort="props.options?.choices == null"
				@select="({ option }) => handleOnSelect(option.value)"
			>
				<template #option="{ option }">
					<input
						type="checkbox"
						class="checkbox checkbox-xs checkbox-neutral pointer-events-none mr-2 rounded-sm"
						:checked="isSelected(option.value)"
					/>
					<span>
						{{ option.label }}
					</span>
					<Tooltip v-if="option.hint" :tooltip="option.hint">
						<!-- Tooltip don't display without a span, for some reasons... -->
						<span class="ml-2">
							<Icon size="sm" :d="mdiInformationOutline" />
						</span>
					</Tooltip>
					<span
						v-if="facets.has(option.value)"
						class="flex-center ml-auto size-4 font-mono text-xs"
					>
						{{ facets.get(option.value) }}
					</span>
				</template>
			</Command>

			<template v-if="selectedCount > 0">
				<div class="bg-border h-px"></div>
				<div class="p-1">
					<button
						class="command-item hover:bg-focus w-full justify-center px-2 text-center"
						@click="clearFilter"
					>
						{{ t("table.clear-filters") }}
					</button>
				</div>
			</template>
		</Popover>
	</div>
</template>
